import React from "react";
import { Star, Check, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

const PricingSection = ({ onGetStarted }) => {
  return (
    <section id="pricing" className="min-h-screen flex items-center bg-gradient-to-b from-gray-50 to-white py-16 lg:py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Top Section - Heading and Description */}
        <div className="grid lg:grid-cols-5 gap-8 mb-12">
          {/* Left - Heading (60%) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-3"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              Grow Organic Traffic <br /> on {" "}
              <span className="bg-gradient-to-r from-green-500 to-green-600 bg-clip-text text-transparent">
                Auto-Pilot
              </span>
            </h2>
          </motion.div>

          {/* Right - Description and Social Proof (40%) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2 flex flex-col justify-center space-y-6"
          >
            <p className="text-gray-600 text-lg leading-relaxed">
              Hyperrank grows your SEO rankings and organic traffic while you focus on growing your business.
            </p>
            
            {/* Social Proof */}
            <div className="flex items-center gap-4">
              <div className="flex -space-x-2">
                <img
                  src="/placeholder.svg?height=48&width=48"
                  alt="User"
                  className="w-12 h-12 rounded-full border-2 border-white shadow-md"
                />
                <img
                  src="/placeholder.svg?height=48&width=48"
                  alt="User"
                  className="w-12 h-12 rounded-full border-2 border-white shadow-md"
                />
                <img
                  src="/placeholder.svg?height=48&width=48"
                  alt="User"
                  className="w-12 h-12 rounded-full border-2 border-white shadow-md"
                />
                <img
                  src="/placeholder.svg?height=48&width=48"
                  alt="User"
                  className="w-12 h-12 rounded-full border-2 border-white shadow-md"
                />
              </div>
              <div>
                <div className="flex gap-0.5 text-yellow-400 mb-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-current" />
                  ))}
                </div>
                <p className="text-sm font-semibold text-gray-900">50k+ Articles Created</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Pricing Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="relative">
            {/* Main Card */}
            <div className="relative bg-white rounded-3xl border-4 border-green-300 overflow-hidden">
              <div className="p-8 lg:p-12">
                <div className="grid lg:grid-cols-5 gap-8 lg:gap-12">
                  
                  {/* Left Section - Pricing Info */}
                  <div className="lg:col-span-2 lg:border-r lg:border-gray-200 lg:pr-12">
                    <div className="mb-6">
                      <h3 className="text-2xl font-bold mb-2">All in One</h3>
                      <p className="text-gray-600 text-sm">For ambitious entrepreneurs</p>
                    </div>

                    <div className="mb-8">
                      <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-6xl font-bold tracking-tight text-gray-900">$99</span>
                        <div className="flex flex-col">
                          <span className="text-gray-400 line-through text-xl">$200</span>
                          <span className="text-gray-600 text-base">/monthly</span>
                        </div>
                      </div>
                    </div>

                    <motion.button 
                      onClick={onGetStarted}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full text-base font-semibold text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg hover:shadow-xl mb-3">
                      Get Started for Free
                      <ArrowRight className="h-5 w-5" />
                    </motion.button>

                    <p className="text-center text-gray-600 text-xs">
                      <span className="font-semibold">Cancel anytime.</span> No questions asked!
                    </p>
                  </div>

                  {/* Right Section - Features Grid */}
                  <div className="lg:col-span-3">
                    <h4 className="font-bold text-lg mb-6 text-gray-900">What's included:</h4>
                    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                      {[
                        { text: "30 Articles a month", desc: "generated and published on auto-pilot" },
                        { text: "Unlimited Users", desc: "in your Organization" },
                        { text: "Auto Keyword Research", desc: "made for you hands-free" },
                        { text: "Integrates with WordPress, Webflow, Shopify, Framer", desc: "and many", link: "other platforms" },
                        { text: "High DR Backlinks", desc: "built for you on auto-pilot through our", link: "Backlink Exchange" },
                        { text: "Relevant YouTube videos", desc: "integrated into articles" },
                        { text: "AI Images", desc: "generated in different styles" },
                        { text: "Articles generated in 150+ languages", desc: "" },
                        { text: "Unlimited AI Rewrites", desc: "" },
                        { text: "Custom Features", desc: "requests" },
                      ].map((feature, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3, delay: 0.6 + i * 0.05 }}
                          className="flex items-start gap-3"
                        >
                          <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mt-0.5">
                            <Check className="h-3 w-3 text-green-600 stroke-[3]" />
                          </div>
                          <span className="text-gray-700 text-sm leading-snug">
                            <span className="font-semibold text-gray-900">{feature.text}</span>
                            {feature.desc && (
                              <>
                                {" "}{feature.desc}
                              </>
                            )}
                            {feature.link && (
                              <span className="text-green-600 font-medium"> {feature.link}</span>
                            )}
                          </span>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                  
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;

